import {
    AmaronEventContainerDispositionEnum,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    amaronEventContainerDispositions,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import {
    AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ID,
    AMARON_PRIORITY_SOURCE_CONFIGS,
    resolveAmaronPriorityDispositionBaseScore,
    resolveAmaronPriorityScore,
    resolveAmaronPrioritySourceConfig,
    type AmaronPrioritySourceConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/AmaronPriority";
import {
    AMARON_PRIORITY_SCORE_MAX,
    AMARON_PRIORITY_SCORE_MIN,
    AMARON_PRIORITY_SCORE_RANGES,
    formatAmaronPriorityLevel,
    type AmaronPriorityMetric,
    type AmaronPriorityLevel,
} from "~/utilities/workspaceSpecific/amaronPriorityMetric";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getOptionalEventContainerDispositionId} from "~/utilities/ottopilotSemanticColumns";

export {
    AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ID,
    AMARON_PRIORITY_SOURCE_CONFIGS,
    resolveAmaronPriorityDispositionBaseScore,
    resolveAmaronPriorityScore,
    resolveAmaronPrioritySourceConfig,
};
export type {AmaronPrioritySourceConfig};

function buildAmaronPriorityMetrics(): Array<AmaronPriorityMetric> {
    const metrics: Array<AmaronPriorityMetric> = [];

    for (const [dispositionId, baseScore] of Object.entries(AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ID)) {
        for (const sourceConfig of AMARON_PRIORITY_SOURCE_CONFIGS) {
            const score = baseScore + sourceConfig.columnIndex;
            const level = resolveAmaronPriorityLevelFromScore(score);
            metrics.push({
                dispositionId: dispositionId as Uuid,
                utmSource: sourceConfig.key === "default" ? null : sourceConfig.key,
                score: score,
                formattedValue: formatAmaronPriorityLevel(level ?? "P3"),
            });
        }
    }

    return metrics;
}

/** Full priority grid: every {@link amaronEventContainerDispositions} id × UTM source column. */
export const AMARON_PRIORITY_METRICS: Array<AmaronPriorityMetric> = buildAmaronPriorityMetrics();

/** Resolves queue metric from UTM source + disposition id (canonical implementation). */
export function getAmaronPriorityMetric(
    utmSource: string | null | undefined,
    dispositionId: Uuid,
): AmaronPriorityMetric | null {
    const score = resolveAmaronPriorityScore(utmSource, dispositionId);
    if (score === null) {
        return null;
    }
    const sourceConfig = resolveAmaronPrioritySourceConfig(utmSource);
    const level = resolveAmaronPriorityLevelFromScore(score);
    return {
        dispositionId: dispositionId,
        utmSource: sourceConfig.key === "default" ? null : sourceConfig.key,
        score: score,
        formattedValue: formatAmaronPriorityLevel(level ?? "P3"),
    };
}

export function formatAmaronPriorityMetricDisplay(metric: AmaronPriorityMetric | null): string {
    if (metric === null) {
        return "";
    }
    return `${metric.score}(${metric.formattedValue})`;
}

/** Maps a non-negative agent queue score to P0–P3; P3 starts at 165 with no upper bound. */
export function resolveAmaronPriorityLevelFromScore(score: number): AmaronPriorityLevel | null {
    if (Number.isFinite(score) === false || score === Number.MAX_SAFE_INTEGER) {
        return null;
    }
    if (score < AMARON_PRIORITY_SCORE_MIN || score > AMARON_PRIORITY_SCORE_MAX) {
        return null;
    }
    if (score <= AMARON_PRIORITY_SCORE_RANGES.P0.max) {
        return "P0";
    }
    if (score <= AMARON_PRIORITY_SCORE_RANGES.P1.max) {
        return "P1";
    }
    if (score <= AMARON_PRIORITY_SCORE_RANGES.P2.max) {
        return "P2";
    }
    return "P3";
}

/** Table/export display: P0, P1, P2, or P3 only; empty when score is null. */
export function formatAmaronSourcePriorityScoreForDisplay(score: number | null | undefined): string {
    if (score === null || score === undefined) {
        return "";
    }
    const level = resolveAmaronPriorityLevelFromScore(score);
    if (level === null) {
        return "";
    }
    return level;
}

export function resolveAmaronUtmSourceFromJourneyAdditionalValues(
    additionalColumnValues: Record<string, unknown>,
): string | null | undefined {
    const raw = additionalColumnValues[String(AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID)];
    if (raw === null) {
        return null;
    }
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    return undefined;
}

/** Numeric queue score persisted on {@link AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID}. */
export function buildAmaronSourcePriorityColumnValue(
    journey: {additionalColumnValues: Record<string, unknown>},
    dispositionId: Uuid,
): number | null {
    const utmSource = resolveAmaronUtmSourceFromJourneyAdditionalValues(journey.additionalColumnValues);
    return resolveAmaronPriorityScore(utmSource, dispositionId);
}

export function buildAmaronSourcePriorityColumnUpdate(
    journey: {additionalColumnValues: Record<string, unknown>},
    dispositionId: Uuid,
): Partial<Record<Uuid, number>> {
    const score = buildAmaronSourcePriorityColumnValue(journey, dispositionId);
    if (score === null) {
        return {};
    }
    return {
        [AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID]: score,
    };
}

/** Default journey disposition when creating a lead from Amaron call schedule / log. */
export const AMARON_NEW_LEAD_DISPOSITION_ID =
    amaronEventContainerDispositions[AmaronEventContainerDispositionEnum.NEW_LEAD].id;

/** Queue score for New Lead + journey UTM source (e.g. ottopilot on agent-created leads). */
export function buildAmaronSourcePriorityColumnUpdateForNewLead(
    additionalColumnValues: Record<string, unknown>,
): Partial<Record<Uuid, number>> {
    return buildAmaronSourcePriorityColumnUpdate(
        {additionalColumnValues: additionalColumnValues},
        AMARON_NEW_LEAD_DISPOSITION_ID,
    );
}

/** Parses numeric score from column value (supports legacy `score(tier)` strings). */
export function parseAmaronSourcePriorityScoreFromStoredValue(raw: unknown): number {
    if (raw === null || raw === undefined) {
        return Number.MAX_SAFE_INTEGER;
    }
    if (typeof raw === "number") {
        if (Number.isFinite(raw) === false) {
            return Number.MAX_SAFE_INTEGER;
        }
        return raw;
    }
    if (typeof raw !== "string" && typeof raw !== "boolean" && typeof raw !== "bigint") {
        return Number.MAX_SAFE_INTEGER;
    }
    const text = String(raw).trim();
    if (text === "") {
        return Number.MAX_SAFE_INTEGER;
    }
    const openParenIndex = text.indexOf("(");
    const scorePart = openParenIndex >= 0 ? text.slice(0, openParenIndex) : text;
    const parsed = Number(scorePart.trim());
    if (Number.isFinite(parsed) === false) {
        return Number.MAX_SAFE_INTEGER;
    }
    return parsed;
}

/** Stored column value only; `null` when missing or not yet persisted. */
export function getAmaronSourcePriorityStoredScore(journey: {
    additionalColumnValues: Record<string, unknown>;
}): number | null {
    const raw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID];
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === "number") {
        if (Number.isFinite(raw) === false) {
            return null;
        }
        return raw;
    }
    if (typeof raw !== "string" && typeof raw !== "boolean" && typeof raw !== "bigint") {
        return null;
    }
    const text = String(raw).trim();
    if (text === "") {
        return null;
    }
    const parsed = parseAmaronSourcePriorityScoreFromStoredValue(raw);
    if (parsed === Number.MAX_SAFE_INTEGER) {
        return null;
    }
    return parsed;
}

/** P0–P3 from stored priority column only. */
export function formatAmaronSourcePriorityForJourney(journey: {
    additionalColumnValues: Record<string, unknown>;
}): string {
    const score = getAmaronSourcePriorityStoredScore(journey);
    return formatAmaronSourcePriorityScoreForDisplay(score);
}

/** Resolves P0–P3 from stored score, or computes score from UTM + disposition when missing. */
export function resolveAmaronLeadPriorityFromJourney(
    journey: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
): AmaronPriorityLevel {
    let score = getAmaronSourcePriorityStoredScore(journey);
    const dispositionId = getOptionalEventContainerDispositionId(journey, eventContainerTemplate);
    if (score === null && dispositionId !== null) {
        score = buildAmaronSourcePriorityColumnValue(journey, dispositionId);
    }
    const level = score !== null ? resolveAmaronPriorityLevelFromScore(score) : null;
    if (level !== null) {
        return level;
    }
    return "P3";
}

export function compareJourneysByAmaronSourcePriorityColumn(
    left: EventContainerWithEvents,
    right: EventContainerWithEvents,
): number {
    const leftScore = parseAmaronSourcePriorityScoreFromStoredValue(
        left.additionalColumnValues[AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID],
    );
    const rightScore = parseAmaronSourcePriorityScoreFromStoredValue(
        right.additionalColumnValues[AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID],
    );
    if (leftScore !== rightScore) {
        return leftScore - rightScore;
    }
    return right.createdAt.localeCompare(left.createdAt);
}

export function sortJourneysByAmaronSourcePriorityColumn<T extends EventContainerWithEvents>(
    rows: Array<T>,
): Array<T> {
    return [...rows].sort(compareJourneysByAmaronSourcePriorityColumn);
}

export type AmaronPriorityUtmTestPattern = {
    label: string;
    utmSource: string | null;
};

/** UTM values / patterns for matrix and manual testing (config order, left → right). */
export function getAmaronPriorityUtmSourceTestPatterns(): Array<AmaronPriorityUtmTestPattern> {
    const patterns: Array<AmaronPriorityUtmTestPattern> = [
        {
            label: "null (API)",
            utmSource: null,
        },
    ];

    for (const sourceConfig of AMARON_PRIORITY_SOURCE_CONFIGS) {
        if (sourceConfig.key === "default") {
            patterns.push({
                label: "(default / empty)",
                utmSource: "",
            });
            continue;
        }
        if (sourceConfig.exactRawPatterns !== undefined) {
            for (const rawPattern of sourceConfig.exactRawPatterns) {
                patterns.push({
                    label: rawPattern,
                    utmSource: rawPattern,
                });
            }
        }
        for (const matchPattern of sourceConfig.matchPatterns) {
            patterns.push({
                label: matchPattern,
                utmSource: matchPattern,
            });
        }
    }

    return patterns;
}

export type AmaronPriorityMatrixDispositionRow = {
    dispositionId: Uuid;
    displayName: string;
    baseScore: number;
};

export function getAmaronPriorityMatrixDispositionRows(): Array<AmaronPriorityMatrixDispositionRow> {
    const rows: Array<AmaronPriorityMatrixDispositionRow> = [];

    for (const [dispositionId, baseScore] of Object.entries(AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ID)) {
        const disposition = Object.values(amaronEventContainerDispositions).find((item) => {
            return item.id === dispositionId;
        });
        rows.push({
            dispositionId: dispositionId as Uuid,
            displayName: disposition !== undefined ? disposition.displayName : dispositionId,
            baseScore: baseScore,
        });
    }

    rows.sort((left, right) => {
        if (left.baseScore !== right.baseScore) {
            return left.baseScore - right.baseScore;
        }
        return left.displayName.localeCompare(right.displayName);
    });

    return rows;
}
